<template>
  <teleport to="body">
    <!-- Backdrop -->
    <transition name="fade">
      <div
        v-if="isOpen"
        class="fixed inset-0 bg-black bg-opacity-40 z-40"
        @click="close"
      />
    </transition>

    <!-- Drawer -->
    <transition name="slide">
      <div
        v-if="isOpen"
        class="fixed top-0 right-0 h-full w-full max-w-md bg-white z-50 flex flex-col shadow-2xl"
      >
        <!-- Header -->
        <div class="flex items-center justify-between px-5 py-4 border-b border-secondary-200">
          <h2 class="text-lg font-bold text-primary-800">
            Shopping Cart
            <span v-if="itemCount > 0" class="ml-2 text-sm font-normal text-secondary-500">({{ itemCount }} items)</span>
          </h2>
          <button class="p-1.5 text-secondary-400 hover:text-primary-800 transition-colors rounded-lg hover:bg-secondary-100" @click="close">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Empty -->
        <div v-if="isEmpty" class="flex-1 flex flex-col items-center justify-center text-center px-6">
          <svg class="w-16 h-16 text-secondary-200 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
              d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13l-1.5 6h13M7 13L5.4 5M10 21a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm9 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
          </svg>
          <p class="text-lg font-semibold text-primary-800 mb-1">Your cart is empty</p>
          <p class="text-sm text-secondary-500 mb-6">Add some construction materials to get started.</p>
          <button
            class="bg-primary-800 hover:bg-accent-300 text-white text-sm font-semibold px-6 py-2.5 rounded-xl transition-colors"
            @click="goShop"
          >
            Browse Products
          </button>
        </div>

        <!-- Items -->
        <div v-else class="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <div
            v-for="item in cartItems"
            :key="item.product.id"
            class="flex gap-3"
          >
            <img
              :src="item.product.images[0]?.url"
              :alt="item.product.name"
              class="w-16 h-16 rounded-lg object-cover flex-shrink-0 border border-secondary-200"
            />
            <div class="flex-1 min-w-0">
              <p class="text-xs text-accent-500 mb-0.5">{{ item.product.category.name }}</p>
              <p class="text-sm font-semibold text-primary-800 leading-snug line-clamp-2">{{ item.product.name }}</p>
              <p class="text-xs text-secondary-400 mt-0.5">{{ formatPrice(item.product.price) }} / {{ item.product.unit }}</p>
              <div class="flex items-center justify-between mt-2">
                <!-- Qty -->
                <div class="flex items-center border border-secondary-200 rounded-lg overflow-hidden text-sm">
                  <button class="px-2 py-1 hover:bg-secondary-100 text-primary-800 transition-colors" @click="updateQty(item.product.id, item.quantity - 1)">−</button>
                  <span class="px-2.5 py-1 border-x border-secondary-200 font-medium text-primary-800 min-w-[2rem] text-center">{{ item.quantity }}</span>
                  <button class="px-2 py-1 hover:bg-secondary-100 text-primary-800 transition-colors" @click="updateQty(item.product.id, item.quantity + 1)">+</button>
                </div>
                <div class="flex items-center gap-2">
                  <span class="text-sm font-bold text-primary-800">{{ formatPrice(item.product.price * item.quantity) }}</span>
                  <button class="text-secondary-300 hover:text-red-500 transition-colors" @click="removeItem(item.product.id)">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div v-if="!isEmpty" class="border-t border-secondary-200 px-5 py-4 space-y-3">
          <div class="flex justify-between text-sm text-secondary-600">
            <span>Subtotal</span><span>{{ formatPrice(subtotal) }}</span>
          </div>
          <div class="flex justify-between text-sm text-secondary-600">
            <span>Delivery</span><span>RWF 5,000</span>
          </div>
          <div class="flex justify-between font-bold text-primary-800 border-t border-secondary-100 pt-2">
            <span>Total</span><span>{{ formatPrice(subtotal + 5000) }}</span>
          </div>
          <button
            class="w-full bg-primary-800 hover:bg-primary-700 text-white font-semibold py-3 rounded-xl transition-colors text-sm"
            @click="goCheckout"
          >
            Proceed to Checkout
          </button>
          <button
            class="w-full text-sm text-secondary-500 hover:text-primary-800 transition-colors py-1"
            @click="close"
          >
            Continue Shopping
          </button>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script lang="ts">
import { defineComponent, computed } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'

export default defineComponent({
  name: 'CartDrawer',
  setup () {
    const store = useStore()
    const router = useRouter()

    const isOpen = computed(() => store.state.cart.isOpen)
    const cartItems = computed(() => store.state.cart.items)
    const isEmpty = computed(() => store.getters['cart/isEmpty'])
    const itemCount = computed(() => store.getters['cart/itemCount'])
    const subtotal = computed(() => store.getters['cart/subtotal'])

    function close () { store.dispatch('cart/toggleCart') }
    function formatPrice (n: number) { return 'RWF ' + n.toLocaleString() }
    function updateQty (productId: string, qty: number) {
      store.dispatch('cart/updateQuantity', { productId, quantity: qty })
    }
    function removeItem (productId: string) {
      store.dispatch('cart/removeItem', productId)
    }
    function goCheckout () {
      close()
      router.push('/checkout')
    }
    function goShop () {
      close()
      router.push('/shop')
    }

    return { isOpen, cartItems, isEmpty, itemCount, subtotal, close, formatPrice, updateQty, removeItem, goCheckout, goShop }
  }
})
</script>

<style scoped>
.fade-enter-active, .fade-leave-active { transition: opacity 0.25s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }

.slide-enter-active, .slide-leave-active { transition: transform 0.3s ease; }
.slide-enter-from, .slide-leave-to { transform: translateX(100%); }
</style>
